import { Component, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';
import { TextModes } from '../../components/ui/controls/text-input/text-input.component';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { ErrorDialogService } from '../../core/error-dialog/error-dialog.service';
import { StorageService } from '../../core/storage/storageService';
import { MerchantService } from '../../models/merchant/merchantService';
import { SignInResponse } from '../../models/users/signInResponse';
import { UserService } from '../../models/users/userService';
import { BasePage } from '../BasePage';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService } from '../../models/admin/adminService';
import { Logging } from '../../core/logging';
import { CustomSnackbarComponent } from '../../components/ui/snackbar/custom-snackbar.component';
import { environment } from '../../../environments/environment';
import { ListItem } from '../../../../../../libs/Shared/components/model/listItem';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  standalone: false,
})
export class SigninComponent extends BasePage {
  @ViewChild('triggerForm', { static: false })
  triggerForm: NgForm | undefined;

  data = {
    email: '',
    password: '',
    newPassword: '',
    newPassword2: '',
  };

  requiresPasswordReset: boolean = false;
  resetCode: string | null = null;
  requires2FA = false;
  forgotPassword: boolean = false;
  sentForgotPasswordEmail: boolean = false;
  //tfaChars: string[] = [];
  tfaChars: string = '';
  userId: string | undefined = undefined;
  //user: User|undefined = undefined;
  isLoading: boolean = false;

  TextModes = TextModes;

  constructor(
    private errorDialog: ErrorDialogService,
    private userService: UserService,
    private merchantService: MerchantService,
    private storage: StorageService,
    private router: Router,
    private uiErrorHandler: UIErrorHandler,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private adminService: AdminService,
  ) {
    super();

    if (this.storage.isSessionActive()) {
      this.router.navigateByUrl('merchant/account');
    } else {
      this.userService.signout();
    }
    this.userService.signout();
    document.location.href = environment.suiteUrl + '/sign-in';
    return;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.resetCode = this.route.snapshot.queryParamMap.get('code');

    if (this.resetCode) {
      this.requiresPasswordReset = true;
    }
  }

  btnSuiteLogin_click() {
    window.location.href = environment.suiteUrl + '/sign-in';
  }

  async btnSigninClick() {
    try {
      this.isLoading = true;
      await this.userService
        .signin(this.data.email, this.data.password)
        .then((response: SignInResponse) => {
          this.handleLoginResponse(response);
        })
        .catch((message) => {
          this.isLoading = false;
          throw 'Unable to sign in with the provided credentials';
        });
    } catch (message) {
      this.uiErrorHandler.handleError(message);
    }
  }

  // This is designed to only listen for backspace events.
  /*
    enter2fa_keyup(event:any, which:number) {
        if (event.key == 'Backspace') {
            if (which == 6) {
                this.tfaChars[which-1] = "";
            }
            else if (which > 1) {
                let next = document.getElementById("tfa" + (which - 1));
                this.tfaChars[which-2] = "";
                next?.focus();
            }
        }
    }
    */
  async enter2fa_keyup(event: any) {
    if (this.tfaChars.trim().length == 6) {
      if (this.tfaChars.trim().length != 6) {
        return;
      }

      if (this.userId) {
        await this.userService
          .authenticate2FA(this.userId, this.tfaChars.trim())
          .then((response: SignInResponse) => {
            this.handleLoginResponse(response);
          })
          .catch((e: any) => {
            console.log('error', e);
            // this.uiErrorHandler.handleError("2-factor authentication failed.");
          });
      } else {
        // This really should never happen, but...
        this.uiErrorHandler.handleError('Your account could not be located.');
      }
    }
  }

  btnResetPassword_click() {
    if (this.data.newPassword != this.data.newPassword2) {
      this.errorDialog.open(
        'Unable to reset password',
        'Your new passwords much match',
      );

      return;
    } else if (this.data.newPassword.trim().length < 12) {
      this.errorDialog.open(
        'Unable to reset password',
        'Your new password must be at least 12 characters',
      );

      return;
    } else if (this.resetCode == null && this.data.password == '') {
      this.errorDialog.open(
        'Unable to reset password',
        'There was a problem reseting your password. Please follow the link in the password reset email you received.',
      );

      return;
    }

    this.userService
      .resetPassword(
        this.data.password,
        this.data.newPassword,
        this.resetCode,
        this.data.email,
      )
      .then((response: SignInResponse) => {
        this.handleLoginResponse(response);
      })
      .catch((e: Error) => {
        this.uiErrorHandler.handleError(e.message);
      });
  }

  btnForgotPassword_click() {
    this.forgotPassword = true;
  }

  btnSendForgotPassword_click() {
    this.userService
      .forgotPassword(this.data.email)
      .then((response) => {
        if (response) {
          this.forgotPassword = false;
          this.sentForgotPasswordEmail = true;
          this.snackBar.openFromComponent(CustomSnackbarComponent, {
            duration: 5000,
            panelClass: 'snackbar-success',
            data: {
              message:
                'A password reset email has been sent to your email address.',
              fontSize: '1.5em',
              iconSize: '50px',
            },
          });
        }
      })
      .catch(() => {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-error',
          data: {
            message: 'There was a problem sending a password reset email.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
        this.sentForgotPasswordEmail = false;
        // do we want to expose to the user that their email was not found?  Probably.
        this.uiErrorHandler.handleError(
          "We couldn't locate an account with that email address.",
        );
      });
  }

  btnCancel_click() {
    this.forgotPassword = false;
    this.requiresPasswordReset = false;
    this.requires2FA = false;
  }

  private async handleLoginResponse(response: SignInResponse) {
    if (response.requires2FA) {
      this.requires2FA = true;
      this.userId = response.userId;
      this.isLoading = false;
      return;
    } else if (response.requiresPasswordReset) {
      this.requiresPasswordReset = true;
      this.data.password = '';
      this.isLoading = false;
      return;
    } else if (response.user) {
      this.storage.clearSession();

      await this.userService.initializeSession(response);

      this.merchantService.syncStatements(this.storage.getMyAccount());

      this.adminService.getMerchantBrands().then((response: string[]) => {
        let brandList = response.map((s) => new ListItem({ id: s, name: s }));
        brandList.push(new ListItem({ id: 'New Brand', name: 'New Brand' }));
        this.storage.setBrandList(brandList);
      });

      this.isLoading = false;
      if (
        this.storage.getSelectedLocation() ||
        response.user.originalRole == 'portal.merchant'
      ) {
        this.router.navigateByUrl('merchant/account');
      } else {
        this.router.navigateByUrl('dashboard');
      }
    } else {
      this.isLoading = false;
      throw 'Unable to sign in with provided credentials';
    }
  }

  getFloatLabelValue(): FloatLabelType {
    return 'auto';
  }

  triggerSubmit() {
    if (!this.triggerForm) {
      //console.warn('triggerForm not assigned a value');
    } else {
      if (this.triggerForm.valid) {
        this.triggerForm.ngSubmit.emit();
      }
    }
  }
}
